import { useState, useCallback } from 'react';

export function useBatchSelection() {
  const [batchEditMode, setBatchEditMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [batchDeleting, setBatchDeleting] = useState(false);

  const toggleBatchEditMode = useCallback(() => {
    setBatchEditMode(prev => !prev);
    setSelectedItems(new Set());
  }, []);

  const toggleSelectItem = useCallback((itemId: string) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback((itemIds: string[]) => {
    setSelectedItems(prev => {
      const allSelected = itemIds.every(id => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        itemIds.forEach(id => next.delete(id));
        return next;
      } else {
        const next = new Set(prev);
        itemIds.forEach(id => next.add(id));
        return next;
      }
    });
  }, []);

  const getSelectAllState = useCallback((itemIds: string[]): 'none' | 'all' | 'partial' => {
    if (itemIds.length === 0) return 'none';
    const selectedCount = itemIds.filter(id => selectedItems.has(id)).length;
    if (selectedCount === 0) return 'none';
    if (selectedCount === itemIds.length) return 'all';
    return 'partial';
  }, [selectedItems]);

  const clearSelection = useCallback(() => {
    setSelectedItems(new Set());
    setBatchEditMode(false);
  }, []);

  return {
    batchEditMode,
    selectedItems,
    batchDeleting,
    setBatchDeleting,
    toggleBatchEditMode,
    toggleSelectItem,
    toggleSelectAll,
    getSelectAllState,
    clearSelection,
  };
}
